import { Box, Typography, Stack, Sheet, Button, LinearProgress, Chip, List, ListItem, ListItemButton, ListItemContent, ListItemDecorator, CircularProgress } from "@mui/joy";
import { useContext, useEffect, useState } from "react";
import { LayoutContext } from "../context";
import { APIGetModules } from "../api";
import { useNavigate, Link as RouterLink } from "react-router";
import { ArrowLeft, BookOpen, CaretRight, CheckCircle, Circle, Stethoscope } from "@phosphor-icons/react";


// ── Book Card ────────────────────────────────────────────────────────────────
function BookCard({ book, navigate }) {
    const [expanded, setExpanded] = useState(false);

    const total = book.topics.length;
    const filled = book.topics.filter(t => t.has_content).length;
    const read = book.topics.filter(t => t.is_read).length;
    const progress = total > 0 ? Math.round((read / total) * 100) : 0;

    return (
        <Sheet
            variant="outlined"
            sx={{
                borderRadius: 'xl',
                overflow: 'hidden',
                transition: 'box-shadow 0.2s ease',
                '&:hover': { boxShadow: 'md' },
            }}
        >
            {/* Book header */}
            <Box
                onClick={() => setExpanded(!expanded)}
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 2,
                    p: 2.5,
                    cursor: 'pointer',
                    userSelect: 'none',
                    background: 'linear-gradient(135deg, #1a1a1a 0%, #2d2c29 100%)',
                    color: '#fff',
                    '&:hover': { opacity: 0.95 },
                    transition: 'opacity 0.15s ease',
                }}
            >
                <Box sx={{
                    width: 48, height: 48, borderRadius: 'lg',
                    bgcolor: 'rgba(255,255,255,0.12)',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    flexShrink: 0,
                }}>
                    <BookOpen size={24} color="#fff" weight="duotone" />
                </Box>

                <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography level="title-md" sx={{ color: '#fff', fontWeight: 700, mb: 0.25 }}>
                        {book.name}
                    </Typography>
                    <Stack direction="row" spacing={1.5} alignItems="center">
                        <Typography level="body-xs" sx={{ color: 'rgba(255,255,255,0.6)' }}>
                            {total} глав
                        </Typography>
                        <Typography level="body-xs" sx={{ color: 'rgba(255,255,255,0.4)' }}>·</Typography>
                        <Typography level="body-xs" sx={{ color: 'rgba(255,255,255,0.6)' }}>
                            {filled} заполнено
                        </Typography>
                        <Typography level="body-xs" sx={{ color: 'rgba(255,255,255,0.4)' }}>·</Typography>
                        <Typography level="body-xs" sx={{ color: '#DA7756' }}>
                            {read}/{total} прочитано
                        </Typography>
                    </Stack>
                </Box>

                <Stack direction="row" alignItems="center" spacing={1.5}>
                    {progress > 0 && (
                        <Chip size="sm" sx={{ bgcolor: 'rgba(218,119,86,0.25)', color: '#DA7756', border: 'none', fontWeight: 600 }}>
                            {progress}%
                        </Chip>
                    )}
                    <Box sx={{
                        color: 'rgba(255,255,255,0.7)',
                        transition: 'transform 0.2s ease',
                        transform: expanded ? 'rotate(90deg)' : 'rotate(0deg)',
                        display: 'flex',
                    }}>
                        <CaretRight size={20} />
                    </Box>
                </Stack>
            </Box>

            {/* Progress bar */}
            {progress > 0 && (
                <LinearProgress
                    determinate
                    value={progress}
                    sx={{
                        borderRadius: 0,
                        '--LinearProgress-thickness': '3px',
                        '--LinearProgress-progressColor': '#DA7756',
                        bgcolor: 'rgba(218,119,86,0.15)',
                    }}
                />
            )}

            {/* Chapters list */}
            {expanded && (
                <List size="sm" sx={{ py: 0.5 }}>
                    {book.topics.map((topic) => (
                        <ListItem key={topic.id} sx={{ py: 0 }}>
                            <ListItemButton
                                component={RouterLink}
                                to={`/modules/${topic.id}`}
                                sx={{
                                    py: 1,
                                    px: 2.5,
                                    gap: 1.5,
                                    borderRadius: 0,
                                    opacity: topic.has_content ? 1 : 0.5,
                                    '&:hover': { bgcolor: 'background.level1' },
                                    transition: 'all 0.15s ease',
                                    textDecoration: 'none',
                                    color: 'inherit',
                                }}
                            >
                                <ListItemDecorator sx={{ minWidth: 22 }}>
                                    {topic.is_read ? (
                                        <CheckCircle size={17} color="#DA7756" weight="fill" />
                                    ) : (
                                        <Circle size={17} color={topic.has_content ? undefined : '#ccc'} />
                                    )}
                                </ListItemDecorator>
                                <ListItemContent>
                                    <Typography
                                        level="body-sm"
                                        sx={{
                                            fontWeight: topic.is_read ? 500 : 400,
                                            color: topic.has_content ? 'text.primary' : 'text.tertiary',
                                        }}
                                    >
                                        {topic.title}
                                    </Typography>
                                </ListItemContent>
                                {!topic.has_content && (
                                    <Chip size="sm" variant="soft" color="neutral" sx={{ fontSize: '0.65rem' }}>
                                        пусто
                                    </Chip>
                                )}
                            </ListItemButton>
                        </ListItem>
                    ))}
                </List>
            )}
        </Sheet>
    );
}


// ── Main Page ────────────────────────────────────────────────────────────────
export default function ResidencyPage() {
    const { token, user } = useContext(LayoutContext);
    const navigate = useNavigate();
    const [residency, setResidency] = useState(null);
    const [loading, setLoading] = useState(true);

    const isAdmin = user?.role === 'admin';

    useEffect(() => {
        loadData();
    }, []);

    const loadData = async () => {
        try {
            const res = await APIGetModules(token);
            if (res.success) {
                const found = (res.tree || []).find(d => d.name === 'IM Residency');
                setResidency(found || null);
            }
        } catch (e) {
            console.error(e);
        } finally {
            setLoading(false);
        }
    };

    if (loading) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '50vh' }}>
                <CircularProgress />
            </Box>
        );
    }

    if (!isAdmin) {
        return (
            <Box sx={{ p: 4, textAlign: 'center' }}>
                <Typography level="h3">Доступ закрыт</Typography>
            </Box>
        );
    }

    const books = residency?.subsections || [];
    const totalModules = books.reduce((s, b) => s + b.topics.length, 0);
    const totalFilled = books.reduce((s, b) => s + b.topics.filter(t => t.has_content).length, 0);
    const totalRead = books.reduce((s, b) => s + b.topics.filter(t => t.is_read).length, 0);

    return (
        <Box sx={{ p: { xs: 2, sm: 3 }, maxWidth: 800, mx: 'auto' }}>
            {/* Back */}
            <Button
                component={RouterLink}
                to="/modules"
                variant="plain"
                color="neutral"
                startDecorator={<ArrowLeft size={16} />}
                sx={{ mb: 2, px: 1, fontWeight: 500 }}
            >
                Библиотека
            </Button>

            {/* Header */}
            <Stack direction="row" alignItems="center" spacing={1.5} sx={{ mb: 0.5 }}>
                <Box sx={{
                    width: 40, height: 40, borderRadius: 'lg',
                    bgcolor: '#1a1a1a',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                }}>
                    <Stethoscope size={22} color="#DA7756" weight="duotone" />
                </Box>
                <Typography level="h2">IM Residency</Typography>
            </Stack>

            <Typography level="body-sm" sx={{ color: 'text.secondary', mb: 3 }}>
                Программа подготовки к резидентуре по внутренней медицине
            </Typography>

            {/* Stats bar */}
            {totalModules > 0 && (
                <Sheet variant="soft" sx={{ p: 2, px: 2.5, borderRadius: 'lg', mb: 3 }}>
                    <Stack direction="row" spacing={3} flexWrap="wrap">
                        <Box>
                            <Typography level="h4" sx={{ fontWeight: 700 }}>{books.length}</Typography>
                            <Typography level="body-xs" sx={{ color: 'text.tertiary' }}>книг</Typography>
                        </Box>
                        <Box>
                            <Typography level="h4" sx={{ fontWeight: 700 }}>{totalModules}</Typography>
                            <Typography level="body-xs" sx={{ color: 'text.tertiary' }}>глав</Typography>
                        </Box>
                        <Box>
                            <Typography level="h4" sx={{ fontWeight: 700, color: totalFilled === totalModules ? '#DA7756' : undefined }}>
                                {totalFilled}
                            </Typography>
                            <Typography level="body-xs" sx={{ color: 'text.tertiary' }}>заполнено</Typography>
                        </Box>
                        <Box>
                            <Typography level="h4" sx={{ fontWeight: 700, color: '#0F7B6C' }}>{totalRead}</Typography>
                            <Typography level="body-xs" sx={{ color: 'text.tertiary' }}>прочитано</Typography>
                        </Box>
                    </Stack>
                </Sheet>
            )}

            {/* Books grid */}
            {books.length === 0 ? (
                <Sheet variant="outlined" sx={{ p: 5, borderRadius: 'xl', textAlign: 'center' }}>
                    <BookOpen size={48} style={{ opacity: 0.3 }} />
                    <Typography level="title-md" sx={{ mt: 1 }}>Книги не добавлены</Typography>
                    <Typography level="body-sm" color="neutral" sx={{ mt: 0.5 }}>
                        Запусти: python3 manage.py create_residency_categories
                    </Typography>
                </Sheet>
            ) : (
                <Stack spacing={2}>
                    {books.map(book => (
                        <BookCard key={book.category_id} book={book} navigate={navigate} />
                    ))}
                </Stack>
            )}
        </Box>
    );
}
